/* eslint-disable n/no-unsupported-features/node-builtins -- this is a browser environment */
/* eslint-disable no-console */
import type { FC, ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type PaletteMode } from '@mui/material'
import { useLocation } from 'react-router-dom'

import { createDhlPalette } from '@dhl-ui/components'
import { dataLayerPublish } from '@dhl/datalayer'
import { isNative } from '@dhl/webview'

import {
  NativeBridgeContext,
  type NativeBridgeContextProps,
  type ReceiveAvatarHandler,
  type ShowNativeBackdropArgs,
} from './context.js'
import { type NativeEvent } from './nativeEvents.js'
import { NativeUpdateDialog } from './NativeUpdateDialog.js'
import { type ColorValue, type FrameColorValue, type NativeFrameConfig, type PreferredColorScheme } from './types.js'
import { type AppUpdateType } from './useAppVersion.js'
import {
  type DownloadEvent,
  type NavigationInfoPayload,
  type OpenAppSettingsPayload,
  type RatingSettings,
  type VibrationType,
  type WebEvent,
} from './webEvents.js'

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (data: string, targetOrigin?: string, transfer?: string) => void
    }
  }
}

const postMessage = (event: WebEvent) => {
  const messageToPost: string = JSON.stringify(event)
  const reactNativeWebview = window.ReactNativeWebView
  if (typeof reactNativeWebview?.postMessage === 'function') {
    reactNativeWebview.postMessage(messageToPost)
  }
}

const setUserId = (userId: string) => {
  postMessage({ type: 'USER_ID', payload: userId })
}

const openBlobInNative = (fileName: string, blob: Blob) => {
  const reader = new FileReader()
  reader.onloadend = () => {
    const message: DownloadEvent = {
      type: 'DOWNLOAD_FILE',
      payload: {
        data: reader.result,
        fileName,
      },
    }
    postMessage(message)
  }
  reader.readAsDataURL(blob)
}

const downloadFileInBrowser = (uri: string, fallbackFileName: string) => {
  const link = document.createElement('a')
  link.setAttribute('download', fallbackFileName)
  link.href = uri
  document.body.appendChild(link)
  link.click()
  link.remove()
}

export const parseContentDisposition = (headerValue: string): string => {
  const [, filename] = /filename="?([^;"]+)/g.exec(headerValue) || []
  return filename
}

const downloadFile = async (uri: string, fallbackFileName: string) => {
  if (!isNative()) {
    downloadFileInBrowser(uri, fallbackFileName)
    return
  }

  const response = await fetch(uri, {
    credentials: 'include',
  })

  const contentDisposition = response.headers.get('content-disposition') || ''
  const fileName = parseContentDisposition(contentDisposition) || fallbackFileName

  try {
    const blob = await response.blob()
    openBlobInNative(fileName, blob)
  } catch (error) {
    console.error(error)
  }
}

const vibrate = (type: VibrationType) => {
  postMessage({
    type: 'VIBRATE',
    payload: {
      type,
    },
  })
}

const shareLink = (title: string, url: string) => {
  postMessage({
    type: 'SHARE',
    payload: { title, url },
  })
}

const navigationInfoChanged = (payload: NavigationInfoPayload) => {
  postMessage({ type: 'NAVIGATION_INFO', payload })
}

const setNativeColorScheme = (scheme: PreferredColorScheme) =>
  postMessage({
    type: 'COLOR_SCHEME',
    payload: scheme,
  })

const isTrackingAllowedKey = 'isTrackingAllowed'

export type UpdateDialogProps = {
  enable?: boolean
  advisedAppVersion: string
  mandatoryAppVersion: string
  onLoaded?: (type: AppUpdateType) => void
  onClickToAppStore?: (type: AppUpdateType) => void
  onClickConfirmUnsupported?: (type: AppUpdateType) => void
  onClickIgnoreUpdate?: (type: AppUpdateType) => void
  topLevelDomain: string
}

type ThemeArgs = {
  preferredColorScheme: 'dark' | 'light' | 'system'
  setPaletteMode: (mode: PaletteMode) => void
}

type Props = {
  children: ReactNode
  disableBackSwipeOn?: string[]
  fullBrightnessPaths?: string[]
  gradientHeaderPaths?: string[]
  nativeFrame?: NativeFrameConfig
  ratingSettings?: RatingSettings
  updateDialog?: UpdateDialogProps
  userId?: string
  isProduction: boolean
  theme: ThemeArgs
}

interface EventNativeEvent extends Event {
  data: NativeEvent
}

const logWithLevel = (level: string) => (message: string) => {
  postMessage({ type: 'LOG', payload: { message, level } })
}

const nativeBridgeBooted = () => {
  postMessage({ type: 'NATIVE_BRIDGE_BOOTED' })
}

const configureGradientHeaders = (paths: string[]) =>
  postMessage({ type: 'CONFIGURE_GRADIENT_HEADERS', payload: paths })

const lightPalette = createDhlPalette('light')
const darkPalette = createDhlPalette('dark')

const resolveColor = (color: FrameColorValue): ColorValue => {
  if (typeof color === 'object') {
    return color
  }

  switch (color) {
    case 'brand':
      return {
        light: lightPalette.components.pageHeader.default,
        dark: darkPalette.components.pageHeader.default,
      }
    case 'none':
      return {
        light: lightPalette.background.default,
        dark: darkPalette.background.default,
      }
    case 'subtle':
      return {
        light: lightPalette.background.paper,
        dark: darkPalette.background.paper,
      }
    default: {
      const exhaustiveCheck: never = color
      throw new Error(`Unknown status bar color: ${exhaustiveCheck}`)
    }
  }
}

const deriveFooterColor = (statusBar?: FrameColorValue): ColorValue => {
  if (typeof statusBar === 'object') {
    return resolveColor('subtle')
  }

  return resolveColor(statusBar === 'none' ? 'none' : 'subtle')
}

const configureNativeFrame = (config: NativeFrameConfig) => {
  postMessage({
    type: 'CONFIGURE_NATIVE_FRAME',
    payload: {
      defaultStatusBar: resolveColor(config.defaultStatusBarColor),
      defaultFooter: config.defaultFooterColor
        ? resolveColor(config.defaultFooterColor)
        : deriveFooterColor(config.defaultStatusBarColor),
      paths: config.paths?.map(({ pattern, statusBar, footer }) => ({
        pattern,
        statusBar: statusBar ? resolveColor(statusBar) : undefined,
        footer: footer ? resolveColor(footer) : deriveFooterColor(statusBar),
      })),
    },
  })
}

const showNativeBackdrop = ({
  color,
  timeout = 225,
  visibleOnFooter = false,
  visibleOnHeader = true,
  ...rest
}: ShowNativeBackdropArgs) => {
  postMessage({
    type: 'SHOW_NATIVE_BACKDROP',
    payload: { ...rest, color, timeout, visibleOnFooter, visibleOnHeader },
  })
}

const hideNativeBackdrop = () => postMessage({ type: 'HIDE_NATIVE_BACKDROP' })

const configureBrightness = (paths: string[]) => postMessage({ type: 'CONFIGURE_BRIGHTNESS', payload: paths })

const requestChangeAvatar = () => postMessage({ type: 'REQUEST_CHANGE_AVATAR' })

const requestNativeTrackingPermission = () => postMessage({ type: 'REQUEST_NATIVE_TRACKING_PERMISSION' })

export const NativeBridge: FC<Props> = ({
  children,
  disableBackSwipeOn,
  fullBrightnessPaths = [],
  gradientHeaderPaths = [],
  nativeFrame,
  ratingSettings,
  updateDialog,
  userId,
  isProduction,
  theme: { preferredColorScheme, setPaletteMode },
}) => {
  const { pathname } = useLocation()
  const [isBooted, setBooted] = useState(false)
  const getSessionIsTrackingAllowed = window.sessionStorage.getItem(isTrackingAllowedKey)
  const [isTrackingAllowed, setTrackingAllowed] = useState<boolean | undefined>(
    getSessionIsTrackingAllowed ? getSessionIsTrackingAllowed === 'true' : undefined
  )
  const [isBackButtonPressed, setBackButtonPressed] = useState(false)
  const [isDisablePushNotificationRequested, setDisablePushNotificationRequested] = useState(false)
  const [isDeniedPushNotificationPermission, setDeniedPushNotificationPermission] = useState(false)
  const [isRefreshRequested, setRefreshRequested] = useState(false)
  const [isSharingLinkSupported, setSharingLinkSupported] = useState(false)
  const [supportedSocialLoginProviders, setSupportedSocialLoginProviders] = useState<string[]>([])
  const [avatarEditSupported, setAvatarEditSupported] = useState(false)
  const receiveAvatarHandler = useRef<ReceiveAvatarHandler | undefined>(undefined)
  const touchStart = useRef<number | null>(null)
  const touchEnd = useRef<number | null>(null)

  const isSocialLoginProviderSupported = useCallback(
    (provider: string) => supportedSocialLoginProviders.includes(provider),
    [supportedSocialLoginProviders]
  )

  const onReceiveAvatar = useCallback((handler: ReceiveAvatarHandler) => {
    receiveAvatarHandler.current = handler
  }, [])

  const onMessage = useCallback(
    (message: EventNativeEvent) => {
      const { type, payload } = message.data

      switch (type) {
        case 'TRACKING': {
          if (!payload) {
            return
          }
          const nativeTracking = !!payload.isTrackingAllowed
          console.log(`Native tracking: ${nativeTracking}`)
          setTrackingAllowed(nativeTracking)
          window.sessionStorage.setItem(isTrackingAllowedKey, nativeTracking.toString())
          break
        }
        case 'BACK':
          setBackButtonPressed(true)
          break
        case 'REFRESH':
          console.log(`Refresh data signal received. Asking components to respond.`)
          setRefreshRequested(true)
          break
        case 'APPEARANCE':
          if (preferredColorScheme === 'system' && payload.colorScheme) {
            setPaletteMode(payload.colorScheme)
          }

          break
        case 'CONFIGURATION':
          setSupportedSocialLoginProviders(payload.socialLogin.supportedProviders)
          setAvatarEditSupported(payload.avatar?.supported ?? false)
          setSharingLinkSupported(payload.sharing?.supported ?? false)
          break
        case 'SET_AVATAR':
          receiveAvatarHandler?.current?.(payload)
          break
        case 'DENIED_PUSH_NOTIFICATION_PERMISSION':
          setDeniedPushNotificationPermission(true)
          break
        case 'DISABLE_PUSH_NOTIFICATION_SETTINGS':
          setDisablePushNotificationRequested(true)
          break
        default:
          console.warn(`Unhandled native event ${type}`)
          break
      }
    },
    [preferredColorScheme, setPaletteMode]
  )

  const requestRating = useCallback(
    (delayInSeconds: number) => {
      if (!ratingSettings) {
        console.warn("The NativeBridge component is missing the 'ratingSettings' argument.")
        return
      }

      postMessage({
        type: 'RATING',
        payload: { ...ratingSettings, delayInSeconds },
      })
    },
    [ratingSettings]
  )

  const requestPushNotificationPermissions = useCallback(
    () => postMessage({ type: 'REQUEST_PUSH_NOTIFICATION_PERMISSION' }),
    []
  )
  const disablePushNotificationSettings = useCallback(
    () => setDisablePushNotificationRequested(true),
    [setDisablePushNotificationRequested]
  )

  const openAppSettings = useCallback(
    ({ page, syncPushNotificationPermissionAfterwards = true }: Partial<OpenAppSettingsPayload> = {}) =>
      postMessage({ type: 'OPEN_APP_SETTINGS', payload: { page, syncPushNotificationPermissionAfterwards } }),
    []
  )

  const isAvatarEditSupported = useCallback(() => avatarEditSupported, [avatarEditSupported])

  const acknowledgeBackButtonPress = useCallback(() => setBackButtonPressed(false), [setBackButtonPressed])
  const acknowledgeDeniedPushNotificationPermission = useCallback(
    () => setDeniedPushNotificationPermission(false),
    [setDeniedPushNotificationPermission]
  )
  const acknowledgeDisablePushNotifications = useCallback(
    () => setDisablePushNotificationRequested(false),
    [setDisablePushNotificationRequested]
  )
  const acknowledgeRefreshAction = useCallback(() => setRefreshRequested(false), [setRefreshRequested])

  const handleRootTouchStart = useCallback((e: TouchEvent) => {
    touchEnd.current = null
    touchStart.current = e.targetTouches[0].clientX
  }, [])

  const handleRootTouchMove = useCallback((e: TouchEvent) => {
    touchEnd.current = e.targetTouches[0].clientX
  }, [])

  const blockNavigationForBackGesture = useCallback(
    (e: TouchEvent) => {
      if (!touchStart.current || !touchEnd.current) {
        return
      }

      const distance = (touchStart.current ?? 0) - (touchEnd.current ?? 0)
      const isBackSwipe = distance > 0

      if (disableBackSwipeOn && disableBackSwipeOn.includes(pathname) && isBackSwipe) {
        e.preventDefault()
      }
    },
    [disableBackSwipeOn, pathname, touchEnd, touchStart]
  )

  useEffect(() => {
    acknowledgeBackButtonPress()
  }, [pathname, acknowledgeBackButtonPress])

  useEffect(() => {
    acknowledgeRefreshAction()
  }, [pathname, acknowledgeRefreshAction])

  useEffect(() => {
    if (!isNative()) {
      return
    }

    if (!window.ReactNativeWebView) {
      console.warn('Native requirements not met.')
      dataLayerPublish('ERROR', { id: 'native-bridge', message: 'window.ReactNativeWebView failed' })
      return
    }

    if (!isProduction) {
      console.trace = logWithLevel('trace')
      console.log = logWithLevel('log')
      console.debug = logWithLevel('debug')
      console.info = logWithLevel('info')
      console.warn = logWithLevel('warn')
      console.error = logWithLevel('error')
      console.info('Setup native logging')
    }

    document.addEventListener('native', onMessage)

    if (!isBooted) {
      nativeBridgeBooted()
      setBooted(true)
    }

    // eslint-disable-next-line consistent-return -- this is an effect cleanup function
    return () => {
      document.removeEventListener('native', onMessage)
    }
  }, [isBooted, onMessage, isProduction])

  useEffect(() => {
    const element = document.querySelector('#root')
    if (isNative()) {
      element?.addEventListener('touchend', blockNavigationForBackGesture)
      element?.addEventListener('touchmove', handleRootTouchMove)
      element?.addEventListener('touchstart', handleRootTouchStart)
    }
    return () => {
      if (isNative()) {
        element?.removeEventListener('touchend', blockNavigationForBackGesture)
        element?.removeEventListener('touchmove', handleRootTouchMove)
        element?.removeEventListener('touchstart', handleRootTouchStart)
      }
    }
  }, [blockNavigationForBackGesture, handleRootTouchMove, handleRootTouchStart])

  useEffect(() => {
    if (isNative() && userId) {
      setTimeout(() => setUserId(userId))
    }
  }, [userId])

  useEffect(() => {
    postMessage({
      type: 'COLOR_SCHEME',
      payload: preferredColorScheme,
    })
  }, [preferredColorScheme])

  useEffect(() => {
    configureGradientHeaders(gradientHeaderPaths)
  }, [gradientHeaderPaths])

  useEffect(() => {
    if (nativeFrame) {
      configureNativeFrame(nativeFrame)
    }
  }, [nativeFrame])

  useEffect(() => {
    configureBrightness(fullBrightnessPaths)
  }, [fullBrightnessPaths])

  const contextValue: NativeBridgeContextProps = useMemo(
    () => ({
      isTrackingAllowed,
      postMessage,
      downloadFile,
      setUserID: setUserId,
      isBackButtonPressed,
      isRefreshRequested,
      acknowledgeBackButtonPress,
      acknowledgeRefreshAction,
      requestRating,
      navigationInfoChanged,
      vibrate,
      setNativeColorScheme,
      isSocialLoginProviderSupported,
      showNativeBackdrop,
      hideNativeBackdrop,
      requestChangeAvatar,
      isAvatarEditSupported,
      onReceiveAvatar,
      isSharingLinkSupported,
      shareLink,
      requestPushNotificationPermissions,
      isDeniedPushNotificationPermission,
      acknowledgeDeniedPushNotificationPermission,
      openAppSettings,
      disablePushNotificationSettings,
      isDisablePushNotificationRequested,
      acknowledgeDisablePushNotifications,
      requestNativeTrackingPermission,
    }),
    [
      acknowledgeBackButtonPress,
      acknowledgeRefreshAction,
      isBackButtonPressed,
      isRefreshRequested,
      isSocialLoginProviderSupported,
      isTrackingAllowed,
      requestRating,
      onReceiveAvatar,
      isAvatarEditSupported,
      isSharingLinkSupported,
      requestPushNotificationPermissions,
      isDeniedPushNotificationPermission,
      acknowledgeDeniedPushNotificationPermission,
      openAppSettings,
      disablePushNotificationSettings,
      isDisablePushNotificationRequested,
      acknowledgeDisablePushNotifications,
    ]
  )

  return (
    <NativeBridgeContext.Provider value={contextValue}>
      {updateDialog?.enable && <NativeUpdateDialog {...updateDialog} />}
      {children}
    </NativeBridgeContext.Provider>
  )
}
